import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import { useEffect, useRef, useState } from "react";
import {
  ResaleListingFormFields,
  ResaleListingSubmitFormFields,
} from "@/modules/property-tracks/utils";
import useResaleListingMultiStepForm from "@/modules/property-tracks/hooks/listings/resale-listing/multi-step-form.hook";

interface ResaleListingFormProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  defaultValues: ResaleListingFormFields;
  onSubmit: (body: ResaleListingSubmitFormFields) => void;
  isSubmitting: boolean;
  trackUid: string;
  header: string;
  defaultStep?: number;
}

export default function ResaleListingForm({
  open,
  setOpen,
  defaultValues,
  onSubmit,
  isSubmitting,
  trackUid,
  header,
  defaultStep,
}: ResaleListingFormProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const [submitIntent, setSubmitIntent] = useState<"save" | "publish" | null>(
    null
  );

  const {
    steps,
    currentStep,
    activeStep,
    clearForms,
    previousStep,
    isLastStep,
    reset,
    handlePublish,
    handleSave,
  } = useResaleListingMultiStepForm({
    handleSubmit: onSubmit,
    defaultValues,
    trackUid,
    defaultStep,
  });

  const handleClose = () => {
    setOpen(false);
    clearForms();
  };

  useEffect(() => {
    if (!open) clearForms();
    else reset(defaultValues);
  }, [open]);

  useEffect(() => {
    modalContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  return (
    open && (
      <>
        <EditFieldModal
          ref={modalContentRef}
          open={open}
          setOpen={setOpen}
          handleCloseModal={handleClose}
          width={480}
          header={header}
          modalFooter={false}
        >
          <>
            <Stepper activeStep={activeStep}>
              {steps.map((step) => (
                <Step key={step.value}>
                  <StepLabel>{step.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {currentStep.content}
            <ModalFooter
              handleClose={handleClose}
              applyButton={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {isLastStep ? (
                    <>
                      <Button
                        sx={(t) => ({
                          textTransform: "none",
                          color: t.nawy.colors.semantic.text.accent,
                          borderColor: t.nawy.colors.semantic.layer.accent,
                        })}
                        onClick={() => {
                          handleSave();
                          setSubmitIntent("save");
                        }}
                        variant="outlined"
                        disabled={isSubmitting}
                        loading={isSubmitting && submitIntent === "save"}
                      >
                        Save
                      </Button>
                      <Button
                        sx={(t) => ({
                          textTransform: "none",
                          color: t.nawy.colors.semantic.text.onColor,
                          backgroundColor: t.nawy.colors.semantic.layer.accent,
                        })}
                        onClick={() => {
                          handlePublish();
                          setSubmitIntent("publish");
                        }}
                        variant="contained"
                        disabled={isSubmitting}
                        loading={isSubmitting && submitIntent === "publish"}
                      >
                        Save & Publish
                      </Button>
                    </>
                  ) : (
                    <Button
                      sx={(t) => ({
                        textTransform: "none",
                        color: t.nawy.colors.semantic.text.onColor,
                        backgroundColor: t.nawy.colors.semantic.layer.accent,
                      })}
                      onClick={() => currentStep.onNext?.()}
                      variant="contained"
                    >
                      Next
                    </Button>
                  )}
                </Box>
              }
              cancelButton={
                activeStep > 0 ? (
                  <Button
                    sx={(t) => ({
                      textTransform: "none",
                      color: t.nawy.colors.semantic.text.accent,
                      borderColor: t.nawy.colors.semantic.layer.accent,
                    })}
                    variant="outlined"
                    onClick={previousStep}
                  >
                    Back
                  </Button>
                ) : (
                  <Box></Box>
                )
              }
            />
          </>
        </EditFieldModal>
      </>
    )
  );
}
